import { css, SerializedStyles } from '@emotion/react'
import { compareDesc, formatISO, isValid, parse } from 'date-fns'
import { debounce } from 'lodash'
import { colors } from 'plume-ui'
import { rgba } from 'polished'
import React, { useState, useRef, useEffect } from 'react'
import { Transition } from 'react-transition-group'
import { TransitionStatus } from 'react-transition-group/Transition'
import { formatDate } from '@/lib/date'
import { DatePicker } from '../DatePicker'
import { DateRange, DateRangeError } from '../DateRangeFilterSelect'
import { useOnClickOutside } from '../../../../hooks/useOnClickOutside'

type FocusedDate = 'from' | 'to' | null

type Props = {
  range: DateRange
  onChange: (range: DateRange) => void
  error: DateRangeError | null
  onError: (error: DateRangeError | null) => void
}

export const DateRangeInput: React.FC<Props> = (props) => {
  const [[fromText, toText], setRangeText] = useState<[string, string]>([
    dateToText(props.range[0]),
    dateToText(props.range[1]),
  ])
  const [focusedDate, setFocusedDate] = useState<FocusedDate>(null)
  const datepickerRef = useRef<HTMLDivElement>(null)
  const fromInputRef = useRef<HTMLInputElement>(null)
  const toInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setRangeText([dateToText(props.range[0]), dateToText(props.range[1])])
  }, [...props.range])

  useOnClickOutside([datepickerRef, fromInputRef, toInputRef], () => {
    setFocusedDate(null)
  })

  const didMount = React.useRef(false)
  React.useEffect(() => {
    if (didMount.current) {
      validateAndChange([fromText, toText], props.error !== null)
    } else {
      didMount.current = true
    }
  }, [fromText, toText])

  const validateAndChange = React.useCallback(
    debounce((textRange: [string, string], hasError: boolean) => {
      const from = parseAsFormat(textRange[0])
      const to = parseAsFormat(textRange[1])
      const error = validateRange([from, to])
      if (error) {
        props.onError(error)
      } else {
        if (hasError) {
          props.onError(null)
        }
        props.onChange([from, to])
      }
    }, 200),
    []
  )

  const handleFocus = (d: FocusedDate) => {
    setFocusedDate(d)
  }

  const handleChangeDatepicker = (date: Date) => {
    const dateText = dateToText(date)
    if (focusedDate === 'from') {
      setRangeText([dateText, toText])
    } else {
      setRangeText([fromText, dateText])
    }
  }

  const handleChangeInput = (focused: FocusedDate, text: string) => {
    if (focused === 'from') {
      setRangeText([text, toText])
    }
    if (focused === 'to') {
      setRangeText([fromText, text])
    }
  }

  const selectedDate =
    focusedDate === 'from'
      ? props.range[0]
      : focusedDate === 'to'
        ? props.range[1]
        : null

  return (
    <div
      css={css`
        padding: 15px 15px 10px;
        border-top: 1px solid ${colors.gray60};
        margin-top: 8px;
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
        `}
      >
        <DateInput
          className="term-start"
          dateText={fromText}
          label="開始"
          onFocus={() => handleFocus('from')}
          ref={fromInputRef}
          onChange={(text) => handleChangeInput('from', text)}
          hasError={props.error?.position.includes('from') ?? false}
        />
        <div
          css={css`
            margin-left: 7px;
            margin-right: 7px;
            font-size: 14px;
            color: ${colors.textLight};
            margin-bottom: 10px;
          `}
        >
          〜
        </div>
        <DateInput
          className="term-end"
          dateText={toText}
          label="終了"
          onFocus={() => handleFocus('to')}
          onChange={(text) => handleChangeInput('to', text)}
          ref={toInputRef}
          hasError={props.error?.position.includes('from') ?? false}
        />
      </div>
      {props.error && (
        <div
          css={css`
            color: ${colors.red60};
            font-size: 13px;
            line-height: 1.4;
            margin-top: 8px;
          `}
        >
          {props.error.message}
        </div>
      )}
      <Transition in={focusedDate !== null} timeout={300}>
        {(transitionState) => (
          <div
            ref={datepickerRef}
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
            `}
          >
            <DatePicker
              selected={selectedDate}
              onChange={handleChangeDatepicker}
              css={datepickerTransitionCSS(transitionState)}
              maxDate={new Date()}
            />
          </div>
        )}
      </Transition>
    </div>
  )
}

const validateRange = (range: DateRange): DateRangeError | null => {
  if (range.some((d) => d && !isValid(d))) {
    const position: Array<'from' | 'to'> = range.every(
      (d) => d !== null && !isValid(d)
    )
      ? ['from', 'to']
      : range[0] && !isValid(range[0])
        ? ['from']
        : ['to']

    return {
      message: '「yyyy/mm/dd」の形式で入力してください',
      position,
    }
  }
  if (range[0] && range[1] && compareDesc(range[0], range[1]) < 0) {
    return {
      message: '範囲の指定が間違っています',
      position: ['from', 'to'],
    }
  }
  return null
}

const datepickerTransitionCSS = (
  status: TransitionStatus
): SerializedStyles | undefined => {
  switch (status) {
    case 'entering':
    case 'entered':
      return css`
        transition:
          max-height 0.15s ease-in,
          opacity 0.3s ease-in;
        max-height: 320px;
        overflow-y: visible;
        opacity: 1;
      `
    case 'exiting':
    case 'exited':
      return css`
        transition:
          max-height 0.2s ease-out,
          opacity 0.15s ease-out;
        max-height: 0;
        overflow-y: hidden;
        opacity: 0;
      `
    default:
      return undefined
  }
}

const DateInput = React.forwardRef<
  HTMLInputElement,
  {
    className?: string
    dateText: string
    label: string
    onFocus: () => void
    onChange: (text: string) => void
    hasError: boolean
  }
>(function DateInputChild(props, ref) {
  return (
    <div>
      <div
        css={css`
          font-size: 11px;
          color: ${colors.textLight};
          margin-bottom: 3px;
        `}
      >
        （{props.label}）
      </div>
      <input
        className={props.className}
        ref={ref}
        value={props.dateText}
        type="text"
        onFocus={props.onFocus}
        onChange={(e) => props.onChange(e.currentTarget.value)}
        css={css`
          width: 128px;
          border: 1px solid ${props.hasError ? colors.red60 : colors.gray60};
          border-radius: 2px;
          height: 32px;
          padding: 10px 7px 8px;
          font-size: 14px;
          color: ${props.hasError ? colors.red60 : colors.text};
          background-color: ${props.hasError
            ? rgba(colors.red60, 0.05)
            : colors.white00};

          &:focus {
            border-color: ${props.hasError ? colors.red60 : colors.cyan60};
            outline: none;
          }
        `}
      />
    </div>
  )
})

const dateToText = (date: Date | null) => {
  return date ? formatDate(formatISO(date), 'yyyy/MM/dd') : ''
}

const parseAsFormat = (dateString: string) => {
  return dateString === '' ? null : parse(dateString, 'yyyy/MM/dd', new Date())
}
